import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

type FieldResult = {
  value: string | number | string[] | null;
  error: string | null;
};

type ValidationRow = {
  is_row_valid: boolean;
  fields: {
    spec_name: FieldResult;
    report_name: FieldResult;
    dut_name: FieldResult;
    condition: FieldResult;
    device_class: FieldResult;
    recipe: FieldResult;
    lsl: FieldResult;
    tgt: FieldResult;
    usl: FieldResult;
    jmp_ymin: FieldResult;
    jmp_ymax: FieldResult;
    jmp_yinc: FieldResult;
    lib_median: FieldResult;
    lib_mean: FieldResult;
    lib_std: FieldResult;
    lib_q90: FieldResult;
    lib_q10: FieldResult;
    lib_n: FieldResult;
  };
};

export type DuplicateRow = {
  key: {
    spec_name: string;
    dut_name: string;
    report_name: string;
    condition: string;
  };
  rows: any[];
};

export type FileError = {
  type: string;
  message: string;
  duplicates?: DuplicateRow[];
};

export type ValidationResponse = {
  file_content_valid: boolean;
  file_error: FileError | null;
  rows: ValidationRow[];
};

type ValidationTableProps = {
  result: ValidationResponse;
};

const columns = [
  { key: "spec_name", label: "Spec Name" },
  { key: "report_name", label: "Report Name" },
  { key: "dut_name", label: "DUT Name" },
  { key: "condition", label: "Condition" },
  { key: "device_class", label: "Device Class" },
  { key: "recipe", label: "Recipe" },
  { key: "lsl", label: "lsl" },
  { key: "tgt", label: "tgt" },
  { key: "usl", label: "usl" },
  { key: "jmp_ymin", label: "jmp_ymin" },
  { key: "jmp_ymax", label: "jmp_ymax" },
  { key: "jmp_yinc", label: "jmp_yinc" },
  { key: "lib_median", label: "lib_median" },
  { key: "lib_mean", label: "lib_mean" },
  { key: "lib_std", label: "lib_std" },
  { key: "lib_q90", label: "lib_q90" },
  { key: "lib_q10", label: "lib_q10" },
  { key: "lib_n", label: "lib_n" },
] as const;

function formatValue(value: FieldResult["value"]) {
  if (value === null || value === undefined) {
    return "";
  }

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return String(value);
}

export function ValidationTable({ result }: ValidationTableProps) {
  const [showValidRows, setShowValidRows] = useState(false);

  const visibleRows = useMemo(() => {
    if (showValidRows) {
      return result.rows;
    }

    return result.rows.filter((row) => !row.is_row_valid);
  }, [result.rows, showValidRows]);

  const validRowCount = result.rows.filter((row) => row.is_row_valid).length;

  const invalidRowCount = result.rows.length - validRowCount;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant={result.file_content_valid ? "default" : "destructive"}>
            {result.file_content_valid ? "Valid file" : "Validation errors"}
          </Badge>

          <span className="text-sm text-muted-foreground">
            {invalidRowCount} invalid row{invalidRowCount === 1 ? "" : "s"}
            {" · "}
            {validRowCount} valid row{validRowCount === 1 ? "" : "s"}
          </span>
        </div>

        {validRowCount > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowValidRows((current) => !current)}
          >
            {showValidRows ? (
              <ChevronDown className="mr-2 h-4 w-4" />
            ) : (
              <ChevronRight className="mr-2 h-4 w-4" />
            )}

            {showValidRows ? "Hide valid rows" : `Show valid rows`}
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.key}>{column.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  className="h-24 text-center text-muted-foreground"
                >
                  {result.rows.length === 0 ? "No rows found" : "All rows are valid"}
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row, rowIndex) => (
                <TableRow
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={rowIndex}
                  className={
                    row.is_row_valid ? "bg-green-100 hover:bg-green-200" : "hover:bg-muted/50"
                  }
                >
                  {columns.map((column) => {
                    const field = row.fields[column.key];
                    const hasError = Boolean(field.error);

                    return (
                      <TableCell
                        key={column.key}
                        className={hasError ? "bg-red-100 text-red-900" : undefined}
                      >
                        <div className="space-y-1">
                          <div className={hasError ? "font-medium text-red-900" : undefined}>
                            {formatValue(field.value) || (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>

                          {field.error && (
                            <div className="text-xs font-medium text-red-700">{field.error}</div>
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
